import { type BasketItem } from '@koala/sdk';
import { ERROR_SLUG } from '@/constants/errorSlugCopy';
import { type FpoErrorEntry, type IErrorResponse } from '@/types/errors';
import { SubmitFailureCause } from '@/types/orderStatus';
import { getErrorDataObject, getFpoErrorEntries } from '@/utils/errorEnvelope';
import { isStatusError } from '@/utils/networkErrors';
import { isPaymentError } from '@/utils/payment';

const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;

const DELIVERY_UNAVAILABLE_ERROR = "Delivery isn't available";
/**
 * TEAM-14642: the two sentences the service actually sends for this one condition.
 * They are kept ONLY as a fallback behind the code check below — and kept as
 * explicit, noun-anchored phrases rather than a bare "temporarily unavailable"
 * substring, because an item-level message reads "Lox Pizza is temporarily
 * unavailable." and must NOT classify as the restaurant being down (AC14). Those
 * are handled through `getUnavailableBasketItemIds`.
 *
 * The submit path sends the "Restaurant" noun; the checkout-init / validate path
 * sends "Location". Matching only the first is the original defect: a diner at a
 * restaurant that had stopped taking orders got a dismissible, retryable dialog
 * instead of the blocking modal.
 */
const RESTAURANT_UNAVAILABLE_PHRASES = [
  'Restaurant is temporarily unavailable',
  'Location is temporarily unavailable',
];

/**
 * Chowly FPO error codes that mean "this restaurant is not accepting orders".
 * coo-api's own `ChowlyFPOErrorCode::isRestaurantUnavailable()` groups exactly
 * these two. They arrive as STRINGS on the wire.
 *
 *   2  — TemporarilyDisabled
 *   32 — RestaurantTemporarilyUnavailable
 */
const RESTAURANT_UNAVAILABLE_CODES = ['2', '32'];

/** Single definition of "this entry says the restaurant is down", shared by the two
 *  readers below so they can never drift apart. */
const isRestaurantUnavailableEntry = (entry: FpoErrorEntry): boolean =>
  entry.code != null && RESTAURANT_UNAVAILABLE_CODES.includes(String(entry.code));

/**
 * The `error_data` entries carrying an FPO code, across BOTH wire shapes (TEAM-14642): the
 * validate path sends an array of entries, the submit path a flat `{ code }`.
 *
 * `getFpoErrorEntries` (TEAM-13882) covers only the array half, and the flat half is folded in
 * here rather than by widening it: `extractPrimaryFpoCode` reads that helper to resolve the
 * sentence a diner sees, and this predicate must not change which copy that rule picks.
 */
const codedErrorEntries = (body: IErrorResponse): FpoErrorEntry[] => {
  const entries = getFpoErrorEntries(body);
  if (entries.length) {
    return entries;
  }
  const flat = getErrorDataObject(body);
  return flat?.code != null ? [flat] : [];
};

const FUNDS_TOTAL_MESSAGE = 'Payment(s) must fund Basket total.';
const TOAST_PAYMENT_INTENT_MESSAGE = 'toast payment intent error';
const BASKET_NOT_VALID_ERROR_CODE = 'basket_not_valid';

// TEAM-14645: chowly-api FPO code for the one TERMINAL delivery-quote failure —
// DeliveryQuoteUnavailable (30): no courier can quote this address at all, so a retry cannot succeed
// (recovery is an address change or switching to pickup). It also carries diner-safe text, so
// isDeliveryUnavailableError catches it too; the code is the robust signal coo-api forwards in
// error_data on the validate stage.
//
// ExpiredDeliveryQuote (14) is deliberately NOT here. It is a *retryable* failure, not a terminal
// one: the courier quote existed but its cached TTL lapsed while the diner sat on checkout
// (chowly-api DispatchCalculator re-fetches a fresh quote on the next validate — TEAM-14644 makes
// the retry re-validate first). Gating it would disable Place Order on a press that would have
// succeeded — the exact dead-button this ticket removes, in reverse. Left unclassified/retryable it
// keeps the button live; if delivery is genuinely gone, the re-validate then surfaces code 30 and
// gates at that point. See the Claude PR review on this branch and chowly-api
// errors/expired_delivery_quote.rb (user_friendly:false) vs delivery_quote_unavailable.rb.
const DELIVERY_QUOTE_FPO_CODES = ['30'];

/** Anything carrying a parsed coo-api envelope — notably `PreparedErrorMessage`. */
export interface ErrorResponseLike {
  error: IErrorResponse | null;
}

/*
 * Every predicate below reads the RAW error body, not the resolved diner message, so TEAM-13882's
 * render rule does not affect them.
 */

export function isDeliveryUnavailableError(errorResponse: ErrorResponseLike): boolean {
  return !!errorResponse.error?.error_description?.includes(DELIVERY_UNAVAILABLE_ERROR);
}

export function getUnavailableBasketItemIds(errorResponse: ErrorResponseLike): string[] {
  const items = getErrorDataObject(errorResponse.error)?.unavailable_items ?? [];
  return items.map((item) => item.basket_item_id).filter((id) => Boolean(id));
}

/**
 * TEAM-14594: the menu endpoint refuses a stored pickup time that falls outside the
 * location's ordering horizon — `now(store tz) + order_ahead_days`, end of day
 * (coo-api `LocationMenuController`). The rejection is HTTP 400 with error code
 * `bad_request` and a `wanted-at` key inside `error_data.failures`.
 *
 * Keyed on the status, the code and that key only. It deliberately matches no message
 * text: TEAM-10728 records that this codebase already string-matches one English
 * message above and asks that no second one be added, and the message here embeds a
 * formatted date, so matching it would be fragile as well as unwanted.
 */
const WANTED_AT_FAILURE_KEY = 'wanted-at';
const BAD_REQUEST_ERROR_CODE = 'bad_request';

export function isWantedAtValidationError(
  error: unknown,
  errorResponse: ErrorResponseLike,
): boolean {
  if (!isStatusError(error, [HTTP_BAD_REQUEST])) {
    return false;
  }

  const body = errorResponse.error;

  if (body?.error !== BAD_REQUEST_ERROR_CODE) {
    return false;
  }

  return WANTED_AT_FAILURE_KEY in (getErrorDataObject(body)?.failures ?? {});
}

/**
 * Does this error mean the restaurant itself has stopped accepting orders?
 *
 * TEAM-14642. Keyed PRIMARILY on the structured FPO code the service already sends,
 * which is unambiguous, and only secondarily on the sentence. The previous
 * implementation compared `error_description` against one exact phrase, so the
 * checkout-init and re-validate paths — which send a different noun — never matched
 * and never opened the blocking modal, no matter how many call sites consulted it.
 *
 * Reads BOTH wire shapes, because both are real: the submit path sends a flat
 * `error_data.code`, while the validate path sends an array of entries each with its
 * own code. `codedErrorEntries` normalises the two.
 *
 * The phrase check is retained as a fallback rather than replaced, so the submit
 * path — the one that works today — behaves exactly as it does now even when no
 * code is present (AC3), and so a POS whose refusal carries no FPO code at all is
 * no worse off than before.
 */
export function isRestaurantUnavailableError(errorResponse: ErrorResponseLike): boolean {
  const body = errorResponse.error;

  if (!body) {
    return false;
  }

  if (codedErrorEntries(body).some(isRestaurantUnavailableEntry)) {
    return true;
  }

  const description = body.error_description ?? '';
  return RESTAURANT_UNAVAILABLE_PHRASES.some((phrase) => description.includes(phrase));
}

/**
 * Chowly FPO code for "the restaurant's POS could not price this order" — TEAM-14493.
 *
 *   13 — TaxCalculationFailure
 *
 * The enum name is historical. chowly-api now returns this code for every Toast pricing failure
 * (tax AND discount), on Pick-Up and Delivery alike, and coo-api passes it through as a
 * `basket_not_valid` 400 carrying the code both in `error_data[].code` and top-level `error_code`.
 */
const PRICING_UNAVAILABLE_CODES = ['13'];

/**
 * Does this error mean the restaurant's POS could not price the order, so no retry from this
 * checkout can succeed? TEAM-14493.
 *
 * Keyed on the structured FPO code ONLY — never on `error_description`, which carries chowly-api's
 * generic fallback text and is exactly the kind of English string TEAM-10728 asks us not to match a
 * second time. Reads both `error_data` wire shapes (`codedErrorEntries`) and the top-level
 * `error_code` coo-api also sends.
 *
 * Deliberately NOT built on `extractPrimaryFpoCode`: that helper exists to pick the one code whose
 * copy a diner reads, so it bails out when an unavailable-items UI is present and logs a Sentry
 * warning for multi-code envelopes. A predicate that decides the checkout end state must be pure
 * and must not change which copy the render rule picks.
 */
export function isPricingUnavailableError(errorResponse: ErrorResponseLike): boolean {
  const body = errorResponse.error;

  if (!body) {
    return false;
  }

  const isPricingCode = (code: unknown): boolean =>
    code != null && PRICING_UNAVAILABLE_CODES.includes(String(code));

  return (
    codedErrorEntries(body).some((entry) => isPricingCode(entry.code)) ||
    isPricingCode(body.error_code)
  );
}

/**
 * TEAM-15690: did coo-api answer 404 for a basket call, meaning it no longer has this basket?
 *
 * coo-api's `BasketController` turns a basket it cannot resolve into `ItemNotFoundException`, which
 * reaches us as a 404 — and from then on every call for that basket 404s the same way. Why coo-api
 * loses the basket is TEAM-15705's to find; this only recognises the outcome.
 *
 * Keyed on the status, like `isLineAlreadyGone` (redux/basket/saga.ts), because the raw `Response`
 * body is read once, by `prepareErrorMessage`, and the callers decide before that. Requires both ids
 * resolved as well: a request built with an unresolved id (`store-locations/undefined/…`) also
 * 404s, and that is a client defect with nothing lost on coo-api's side
 * (docs/MENU_LOCATION_ID_GUARDS.md).
 *
 * A 404 that carries an FPO code is NOT a lost basket. coo-api's `convertFromItemNotFound` sends
 * `error_data: []` and no `error_code`; a coded body is the ordering engine naming a cause
 * (restaurant unavailable, pricing, …) with an upstream status, and that cause's own, more specific
 * handling must win. wretch has already parsed a JSON body onto `error.json` by the time we see it.
 */
export function isBasketNotFoundError(
  error: unknown,
  basketId: string | null | undefined,
  locationId: number | string | null | undefined,
): boolean {
  if (!basketId || !locationId || !isStatusError(error, [HTTP_NOT_FOUND])) {
    return false;
  }
  const body = (error as { json?: IErrorResponse | null }).json;
  return !body || (codedErrorEntries(body).length === 0 && body.error_code == null);
}

/**
 * The diner-facing reason the service supplied for an unavailable restaurant, if any.
 *
 * TEAM-14642: returns the entry's `detail`, and ONLY when the service has flagged
 * it `user_friendly`. Those are the real field roles — `user_friendly` is a
 * boolean gate and `detail` carries the sentence; chowly-api's TemporarilyDisabled
 * sets `user_friendly: true` with `detail: 'Location is temporarily unavailable.'`.
 * Honouring the gate is the point: an entry the service has NOT vetted is internal
 * text and must not be put in front of a diner.
 *
 * Deliberately does NOT fall back to the top-level `error_description`. On the
 * submit path coo-api sends a flat `{code}` with no detail at all, so falling back
 * there would replace the modal's actionable line ("Please try again later or
 * contact the restaurant directly.") with "Restaurant is temporarily unavailable",
 * which merely restates the heading — and would change the submit path AC3 says is
 * untouched. Returning undefined keeps that path exactly as it is today.
 *
 * Only entries carrying a restaurant-unavailable code are consulted, so a response
 * that mixes causes cannot put an unrelated entry's text under the modal's heading.
 */
export function getRestaurantUnavailableReason(
  errorResponse: ErrorResponseLike,
): string | undefined {
  const body = errorResponse.error;

  if (!body) {
    return undefined;
  }

  // Matches on the code AND on there being a usable reason. Matching the code
  // alone would stop at the first restaurant-unavailable entry and, if that one
  // were unvetted or detail-less, discard a vetted reason sitting behind it —
  // losing a sentence the service did supply.
  const match = codedErrorEntries(body).find(
    (entry) => isRestaurantUnavailableEntry(entry) && !!entry.user_friendly && !!entry.detail,
  );

  return match?.detail ?? undefined;
}

/**
 * TEAM-14292: coo-api rejects an invalid-format email (e.g. "someone@icloud" — no TLD) at
 * validate/submit time with a 400 bad_request carrying an `email` key in `error_data.failures`.
 *
 * Keyed on the status code, the error code, and the structured failures key — no English
 * string matching. TEAM-10728 records that this codebase already string-matches one English
 * message (isRestaurantUnavailableError) and asks that no second one be added; the structured
 * key honours that decision.
 */
const EMAIL_FAILURE_KEY = 'email';

export function isInvalidEmailError(error: unknown, errorResponse: ErrorResponseLike): boolean {
  if (!isStatusError(error, [HTTP_BAD_REQUEST])) {
    return false;
  }

  const body = errorResponse.error;

  if (body?.error !== BAD_REQUEST_ERROR_CODE) {
    return false;
  }

  return EMAIL_FAILURE_KEY in (getErrorDataObject(body)?.failures ?? {});
}

/**
 * TEAM-14292: fallback diner-facing copy for invalid-email errors.
 *
 * The saga reads `errorResponse.error?.user_message` off the already-parsed
 * envelope first — if coo-api sends `user_message` on the 400 (e.g. "Did you
 * mean bnarina@gmail.com?"), the diner sees it without an FE release. These
 * constants are the `||` fallback when coo-api sends no `user_message`.
 *
 * Two variants: validate (before payment) and submit (after card authorization).
 */
export const INVALID_EMAIL_USER_MESSAGE =
  'The email address on your account is not valid. Please update your email in your account settings before placing an order.';

export const INVALID_EMAIL_SUBMIT_MESSAGE =
  'The email address on your account is not valid. A temporary hold may have been placed on your payment method. Please update your email in your account settings and try again.';

/**
 * TEAM-14645: pull FPO error codes out of `error_data`, which coo-api sends in more than one shape
 * — the raw FPO array `[{ code, ... }]` for a BasketNotValid with FPO errors, or an object `{ code }`
 * for the restaurant-unavailable special case. Typed as `unknown` because IErrorResponse only models
 * the object form; this reads either without widening that type across the codebase.
 */
function getErrorDataCodes(errorResponse: ErrorResponseLike): string[] {
  const data = errorResponse.error?.error_data as unknown;
  if (!data) {
    return [];
  }
  const entries = Array.isArray(data) ? data : [data];
  return entries
    .map((entry) =>
      entry && typeof entry === 'object' && 'code' in entry
        ? String((entry as { code: unknown }).code)
        : null,
    )
    .filter((code): code is string => code != null && code !== 'undefined');
}

/**
 * TEAM-14645: classify a checkout-submit failure against the taxonomy the ticket defines,
 * using only the signals the storefront actually receives today.
 *
 * FUNDS_TOTAL is matched before the payment-decline heuristic because the upstream FPO
 * "PaymentError" code is overloaded (card decline vs. order-total changed) and only the
 * free-text message separates them — a total change must stay retryable. Card declines
 * are matched on free text (`isPaymentError`) because no canonical `decline_reason` reaches
 * the storefront (TEAM-13137/13227 are unbuilt); a miss falls through to UNCLASSIFIED, which
 * preserves today's behavior — the fail-safe direction. Delivery-quote and card declines are
 * checked before the generic `basket_not_valid` bucket because at the validate stage coo-api
 * surfaces both carrying `error: 'basket_not_valid'` as the top-level code.
 */
export function classifySubmitFailure(
  errorResponse: ErrorResponseLike & { detail?: string },
  { toastInitialized = false }: { toastInitialized?: boolean } = {},
): SubmitFailureCause {
  // TEAM-13882: read the RAW `detail`, not `message` — `message` is now the resolved diner copy and
  // no longer carries the backend wording these substrings look for.
  const detail = errorResponse.detail ?? '';

  // Retryable — a fresh validate can make these succeed (TEAM-14644).
  if (detail.includes(FUNDS_TOTAL_MESSAGE)) {
    return SubmitFailureCause.FUNDS_TOTAL;
  }
  if (toastInitialized && detail.toLowerCase().includes(TOAST_PAYMENT_INTENT_MESSAGE)) {
    return SubmitFailureCause.TOAST_PAYMENT_INTENT;
  }

  // Terminal — no retry against the same address / card can succeed.
  // DeliveryQuoteUnavailable (no courier for this address) via its diner-safe text, or via FPO
  // code 30 when coo-api forwards it in error_data on the validate stage. ExpiredDeliveryQuote (14)
  // is intentionally excluded (see DELIVERY_QUOTE_FPO_CODES) — it is retryable and falls through to
  // the retryable/unclassified buckets below so Place Order stays live.
  if (
    isDeliveryUnavailableError(errorResponse) ||
    getErrorDataCodes(errorResponse).some((code) => DELIVERY_QUOTE_FPO_CODES.includes(code))
  ) {
    return SubmitFailureCause.DELIVERY_QUOTE;
  }
  if (isPaymentError(detail)) {
    return SubmitFailureCause.PAYMENT_DECLINED;
  }

  if (errorResponse.error?.error === BASKET_NOT_VALID_ERROR_CODE) {
    return SubmitFailureCause.BASKET_NOT_VALID;
  }

  return SubmitFailureCause.UNCLASSIFIED;
}

const TERMINAL_SUBMIT_FAILURE_CAUSES: readonly SubmitFailureCause[] = [
  SubmitFailureCause.PAYMENT_DECLINED,
  SubmitFailureCause.DELIVERY_QUOTE,
];

/**
 * TEAM-14645: a terminal cause means retrying against the same card/address cannot work, so
 * the Place Order button stays disabled until the diner changes what failed.
 */
export function isTerminalSubmitFailure(cause?: SubmitFailureCause | null): boolean {
  return !!cause && TERMINAL_SUBMIT_FAILURE_CAUSES.includes(cause);
}

/**
 * TEAM-14645: decide whether Place Order should be blocked for a terminal cause, given the
 * current payment method. A PAYMENT_DECLINED only blocks when there is a card charge the diner can
 * change:
 * - Digital wallet: its sheet re-opens FROM the Place Order button (no separate card form, and the
 *   disabled container sets `pointer-events: none`), so blocking dead-ends checkout — a re-tap is
 *   the recovery, not "the same card".
 * - No-charge basket (a gift card covers the total, or a zero total): there is no card-entry surface
 *   at all, so nothing can dispatch a cause-clearing action — blocking would strand it forever.
 * A plain card/token decline still blocks (recovery is re-entering the card form, which clears the
 * cause), and DELIVERY_QUOTE always blocks (recovery is an address change, reachable on any basket).
 */
export function shouldBlockPlaceOrder(
  cause: SubmitFailureCause | null | undefined,
  {
    isDigitalWalletPayment = false,
    isNoChargeBasket = false,
  }: { isDigitalWalletPayment?: boolean; isNoChargeBasket?: boolean } = {},
): boolean {
  if (!isTerminalSubmitFailure(cause)) {
    return false;
  }
  if (
    cause === SubmitFailureCause.PAYMENT_DECLINED &&
    (isDigitalWalletPayment || isNoChargeBasket)
  ) {
    return false;
  }
  return true;
}

/**
 * A diner tried to sign up (or create an account at checkout) with an email or phone that is already
 * registered. The backend rejects it — observed `error` codes are `duplicate_account` and
 * `bad_request` — with an `error_description` of "Email already in use." or "Phone number already
 * associated with an account." (the trailing period varies). Matching the text rather than the code
 * is deliberate: the code is inconsistent, the wording is not.
 *
 * TEAM-13882: this is the single implementation. `auth/saga.ts` uses it to drive the "please sign in
 * instead" toast and `errorReporter.ts` uses it to downgrade the Sentry severity; those two were
 * separate copies whose own comments claimed they were "kept in lockstep", which is not a property
 * duplicated code has. It takes the raw upstream text (`detail` / `error_description`) because the
 * resolved diner message no longer contains the backend's wording.
 */
export function getDuplicateAccountKind(rawErrorText?: string | null): 'email' | 'phone' | null {
  const haystack = (rawErrorText ?? '').toLowerCase();
  if (haystack.includes('email') && haystack.includes('already in use')) {
    return 'email';
  }
  if (haystack.includes('phone') && haystack.includes('already associated')) {
    return 'phone';
  }
  return null;
}

/**
 * Whether this envelope is coo-api's `required_option_missing` rejection — TEAM-15138.
 *
 * Matched on the slug, never on `error_description`. The message coo-api sends
 * ("Product option group Rice requires a minimum of 1 choices.") interpolates an operator-authored
 * group name, so a substring table over it could never be complete — which is exactly why
 * coo-api#368 introduced a slug and a structured `error_data` for this in the first place.
 */
export function isRequiredOptionMissingError(errorResponse: ErrorResponseLike): boolean {
  return errorResponse.error?.error === ERROR_SLUG.RequiredOptionMissing;
}

/**
 * Which cart line coo-api's `required_option_missing` is complaining about — TEAM-15138.
 *
 * Returns a position in `basket.content.basket_items`, or `null` when the envelope does not
 * identify one.
 *
 * ## The id-first rule, and why the index is what actually does the work
 *
 * coo-api#368's contract is "match on `basket_item_id` when present, else `basket_item_index`", and
 * that is implemented here. But in this app the id half is dead on arrival: `assignBasketItemID`
 * runs *after* validation server-side, so the id is null on `createBasket` and absent on `addItem`,
 * and `updateItem` — the one route that would populate it — has no production callers here. Every
 * one of the 447 reported occurrences came through `createBasket`, where `basket_item_index` is the
 * only handle that exists.
 *
 * The id branch is kept because it is cheap and correct, not because anything exercises it. Do not
 * build UI that depends on it.
 *
 * ## Why `product_id` + `item_name` is not a fallback
 *
 * coo-api sends both, and they are tempting. They are also ambiguous the moment the same product
 * appears twice in a cart with different options — which is the ordinary case for the products this
 * failure happens on. Guessing the wrong line would reopen the wrong item and leave the real one
 * broken, so an unidentifiable line resolves to `null` and the caller falls back to generic
 * handling.
 *
 * ## Why the index can be trusted
 *
 * `basket_item_index` is a position in the array the client sent. On the `createBasket` path that
 * array is `patchBasketProductOptions(basketOrder).basket_items`, and `patchBasketProductOptions`
 * is a straight `.map` — no filter, no reorder — so position N in the request is position N in
 * `content.basket_items`, which is the index `customizeActions.setProduct` expects. Bounds are
 * still checked: the cart can have changed between the request and the response.
 */
export function resolveRequiredOptionBasketItemIndex(
  errorResponse: ErrorResponseLike,
  basketItems: BasketItem[],
): number | null {
  const data = getErrorDataObject(errorResponse.error);
  if (!data) {
    return null;
  }

  if (data.basket_item_id) {
    const byId = basketItems.findIndex((item) => item.id === data.basket_item_id);
    if (byId !== -1) {
      return byId;
    }
  }

  const index = data.basket_item_index;
  if (typeof index === 'number' && index >= 0 && index < basketItems.length) {
    return index;
  }

  return null;
}

/** The option group coo-api named, for an affordance label. Never rendered as a sentence. */
export function getRequiredOptionGroupName(errorResponse: ErrorResponseLike): string | null {
  return getErrorDataObject(errorResponse.error)?.option_group_name ?? null;
}
